import React, { useState, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Search, MapPin, Loader2, Navigation } from "lucide-react";
import { toast } from "sonner";

const AddressSearch = ({ value, onChange, onAddressSelect, label = "Adresse" }) => {
  const [searchQuery, setSearchQuery] = useState(value || "");
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [debounceTimer, setDebounceTimer] = useState(null);

  // Synchroniser searchQuery avec value prop quand elle change
  useEffect(() => {
    setSearchQuery(value || "");
  }, [value]);

  // Real French Address API integration
  const searchAddresses = async (query) => {
    if (query.length < 3) return [];

    try {
      const response = await fetch(`https://api-adresse.data.gouv.fr/search/?q=${encodeURIComponent(query)}&limit=10`);
      const data = await response.json();
      
      return data.features?.map(feature => ({
        id: feature.properties.id,
        full_address: feature.properties.label,
        name: feature.properties.name,
        housenumber: feature.properties.housenumber,
        street: feature.properties.street,
        postcode: feature.properties.postcode,
        city: feature.properties.city,
        context: feature.properties.context,
        score: feature.properties.score,
        coordinates: feature.geometry.coordinates // [longitude, latitude]
      })) || [];
    } catch (error) {
      console.error("Error searching addresses:", error);
      toast.error("Erreur lors de la recherche d'adresse");
      return [];
    }
  };

  const handleSearch = async (query) => {
    if (query.length < 3) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    setLoading(true);
    try {
      const results = await searchAddresses(query);
      setSuggestions(results);
      setShowSuggestions(true);
    } catch (error) {
      console.error("Error searching addresses:", error);
      toast.error("Erreur lors de la recherche d'adresse");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    
    // Ensure onChange is a function before calling
    if (typeof onChange === 'function') {
      onChange(value);
    }
    
    // Clear previous timer
    if (debounceTimer) {
      clearTimeout(debounceTimer);
    }
    
    // Set new timer for debounced search
    const newTimer = setTimeout(() => {
      handleSearch(value);
    }, 300);
    
    setDebounceTimer(newTimer);
  };

  const handleSelectAddress = (address) => {
    setSearchQuery(address.full_address);
    
    // Ensure onChange is a function before calling
    if (typeof onChange === 'function') {
      onChange(address.full_address);
    }
    
    setShowSuggestions(false);
    
    // Pass complete address data to parent
    if (typeof onAddressSelect === 'function') {
      onAddressSelect({
        adresse: address.full_address,
        numero: address.housenumber || "",
        rue: address.street || "",
        code_postal: address.postcode || "",
        ville: address.city || "",
        coordonnees: address.coordinates,
        score: address.score
      });
    }
  };

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }
    };
  }, [debounceTimer]);

  const getScoreColor = (score) => {
    if (score >= 0.8) return "text-emerald-600";
    if (score >= 0.6) return "text-blue-600";
    if (score >= 0.4) return "text-amber-600";
    return "text-red-600";
  };

  return (
    <div className="relative">
      <Label htmlFor="address">{label}</Label>
      <div className="relative">
        <Input
          id="address"
          type="text"
          placeholder="Tapez une adresse (ex: 10 rue de la paix, Paris)..."
          value={searchQuery}
          onChange={handleInputChange}
          className="pr-10"
        />
        <div className="absolute inset-y-0 right-0 flex items-center pr-3">
          {loading ? (
            <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
          ) : (
            <Search className="w-4 h-4 text-gray-400" />
          )}
        </div>
      </div>

      {/* Suggestions Dropdown */}
      {showSuggestions && suggestions.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
          {suggestions.map((address, index) => (
            <div
              key={address.id || index}
              className="flex items-start space-x-3 p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
              onClick={() => handleSelectAddress(address)}
            >
              <MapPin className="w-5 h-5 text-blue-500 mt-0.5" />
              <div className="flex-1 min-w-0">
                <div className="font-medium text-gray-900 truncate">
                  {address.full_address}
                </div>
                {address.context && (
                  <div className="text-sm text-gray-500">
                    {address.context}
                  </div>
                )}
                <div className="flex items-center justify-between mt-1">
                  <div className="text-xs text-blue-600">
                    Code postal: {address.postcode} • {address.city}
                  </div>
                  <div className={`text-xs font-medium ${getScoreColor(address.score)}`}>
                    {Math.round(address.score * 100)}% match
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* No results message */}
      {showSuggestions && suggestions.length === 0 && searchQuery.length >= 3 && !loading && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg p-3">
          <div className="text-sm text-gray-500 text-center flex items-center justify-center space-x-2">
            <Navigation className="w-4 h-4" />
            <span>Aucune adresse trouvée pour "{searchQuery}"</span>
          </div>
        </div>
      )}

      {/* Info note about API */}
      {showSuggestions && (
        <div className="mt-2 text-xs text-gray-400 flex items-center space-x-1">
          <MapPin className="w-3 h-3" />
          <span>Données officielles de la Base Adresse Nationale</span>
        </div>
      )}
    </div>
  );
};

export default AddressSearch;